import { useState, useEffect } from 'react';
import { Search, Lock, Unlock, Loader, CheckCircle, XCircle, Smartphone, AlertCircle, RefreshCw, Zap, Shield } from 'lucide-react';
import { Device } from '../lib/supabase';
import { useDevices } from '../hooks/useDevices';
import { useAuditLog } from '../hooks/useAuditLog';

export function MobileDeviceControl() {
  const { devices, updateDevice, loading } = useDevices();
  const { logAction } = useAuditLog();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Filter devices based on search term
  const filteredDevices = devices.filter(device =>
    device.serial_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.device_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.owner_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Clear action result after 3 seconds
  useEffect(() => {
    if (actionResult) {
      const timer = setTimeout(() => setActionResult(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionResult]);

  const handleDeviceAction = async (device: Device, action: 'lock' | 'unlock') => {
    setActionLoading(true);
    setActionResult(null);

    try {
      const newLockState = action === 'lock';
      const result = await updateDevice(device.id, { is_locked: newLockState });

      if (result.success) {
        // Log the action
        await logAction(
          action === 'lock' ? 'lock_device' : 'unlock_device',
          'device_management',
          {
            resourceType: 'device',
            resourceId: device.id,
            details: {
              device_name: device.device_name,
              serial_number: device.serial_number,
              action: action,
              previous_state: device.is_locked ? 'locked' : 'unlocked',
              new_state: newLockState ? 'locked' : 'unlocked',
              interface: 'mobile'
            },
            success: true
          }
        );

        setActionResult({
          type: 'success',
          message: `Device ${action}ed successfully`
        });

        // Update selected device if it's the same one
        if (selectedDevice?.id === device.id) {
          setSelectedDevice({ ...device, is_locked: newLockState });
        }
      } else {
        throw new Error(result.error || `Failed to ${action} device`);
      }
    } catch (error) {
      console.error(`Failed to ${action} device:`, error);
      
      // Log the failed action
      await logAction(
        action === 'lock' ? 'lock_device' : 'unlock_device',
        'device_management',
        {
          resourceType: 'device',
          resourceId: device.id,
          details: {
            device_name: device.device_name,
            serial_number: device.serial_number,
            action: action,
            interface: 'mobile'
          },
          success: false,
          errorMessage: error instanceof Error ? error.message : 'Unknown error'
        }
      );

      setActionResult({
        type: 'error',
        message: `Failed to ${action} device`
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800/95 backdrop-blur-xl shadow-2xl sticky top-0 z-10 border-b border-gray-700/50">
        <div className="px-6 py-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Smartphone className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Device Control</h1>
              <p className="text-gray-400 text-sm">Quick actions for mobile</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search devices, owners, or serial numbers..."
              className="w-full pl-12 pr-4 py-4 text-lg bg-gray-700/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500/50 transition-all duration-200 backdrop-blur-sm"
            />
          </div>
        </div>
      </div>

      {/* Action Result */}
      {actionResult && (
        <div className="px-6 py-3">
          <div className={`flex items-center space-x-3 p-4 rounded-2xl backdrop-blur-sm border ${
            actionResult.type === 'success'
              ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300'
              : 'bg-red-500/20 border-red-500/30 text-red-300'
          } animate-in slide-in-from-top-2 duration-300`}>
            {actionResult.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            ) : (
              <XCircle className="w-5 h-5 text-red-400" />
            )}
            <span className="font-medium">{actionResult.message}</span>
          </div>
        </div>
      )}

      {/* Device List */}
      <div className="px-6 pb-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mb-4">
              <Loader className="w-8 h-8 animate-spin text-blue-400" />
            </div>
            <p className="text-gray-400">Loading devices...</p>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-gray-500" />
            </div>
            <p className="text-gray-400 text-lg font-medium mb-2">
              {searchTerm ? 'No devices found' : 'No devices available'}
            </p>
            <p className="text-gray-500 text-sm">
              {searchTerm ? 'Try adjusting your search terms' : 'Add some devices to get started'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDevices.map((device) => (
              <div
                key={device.id}
                className="bg-gray-800/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 overflow-hidden hover:border-gray-600/50 transition-all duration-300"
              >
                {/* Device Info */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-lg truncate mb-1">
                        {device.device_name || 'Unnamed Device'}
                      </h3>
                      <p className="text-gray-300 truncate mb-1">
                        {device.owner_name}
                      </p>
                      <p className="text-xs font-mono text-gray-500 bg-gray-700/50 px-2 py-1 rounded-lg inline-block">
                        {device.serial_number}
                      </p>
                    </div>
                    <div className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-semibold shadow-lg ${
                      device.is_locked
                        ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                        : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    }`}>
                      {device.is_locked ? (
                        <>
                          <Lock className="w-4 h-4" />
                          <span>Locked</span>
                        </>
                      ) : (
                        <>
                          <Unlock className="w-4 h-4" />
                          <span>Unlocked</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleDeviceAction(device, 'lock')}
                      disabled={actionLoading || device.is_locked}
                      className={`group relative flex items-center justify-center space-x-2 py-3 px-4 rounded-xl font-medium transition-all duration-300 transform ${
                        device.is_locked
                          ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed border border-gray-600/30'
                          : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 active:scale-95 shadow-lg hover:shadow-xl border border-red-400/30'
                      }`}
                    >
                      {actionLoading ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Lock className={`w-4 h-4 ${!device.is_locked ? 'group-hover:scale-110 transition-transform' : ''}`} />
                      )}
                      <span className="text-base">Lock</span>
                      {!device.is_locked && (
                        <div className="absolute inset-0 bg-gradient-to-r from-red-400/0 via-red-400/20 to-red-400/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      )}
                    </button>

                    <button
                      onClick={() => handleDeviceAction(device, 'unlock')}
                      disabled={actionLoading || !device.is_locked}
                      className={`group relative flex items-center justify-center space-x-2 py-3 px-4 rounded-xl font-medium transition-all duration-300 transform ${
                        !device.is_locked
                          ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed border border-gray-600/30'
                          : 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 active:scale-95 shadow-lg hover:shadow-xl border border-emerald-400/30'
                      }`}
                    >
                      {actionLoading ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Unlock className={`w-4 h-4 ${device.is_locked ? 'group-hover:scale-110 transition-transform' : ''}`} />
                      )}
                      <span className="text-base">Unlock</span>
                      {device.is_locked && (
                        <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/0 via-emerald-400/20 to-emerald-400/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={() => window.location.reload()}
          className="group w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl shadow-xl hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center transform hover:scale-105 active:scale-95 border border-blue-400/30"
        >
          <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
        </button>
      </div>
    </div>
  );
}
